import {Component, Input, OnChanges, SimpleChanges}                              from '@angular/core';
import {MatDialog}                                                                 from '@angular/material/dialog';
import {MatSnackBar}                                                               from '@angular/material/snack-bar';
import {PermissionsService}                                                        from '@src/app/core/services/permissions.service';
import {DialogComponent}                                                          from '@src/app/shared/components/dialog/dialog.component';
import {DocumentParentType, OperationalDocument, operationalDocumentCategories}   from '@src/app/shared/models/document.model';
import {DocumentsService}                                                         from '@src/app/shared/services/documents.service';
import {DocumentUploadDialogComponent}                                            from '@src/app/shared/components/panels/documents/document-upload-dialog.component';
import {finalize, firstValueFrom}                                                 from 'rxjs';

type DocumentVisibility = 'customer' | 'app' | 'report';

@Component({
    standalone: false,
    selector: 'app-documents-panel',
    templateUrl: './documents-panel.component.html',
    styleUrls: ['./documents-panel.component.scss'],
})
export class DocumentsPanelComponent implements OnChanges
{
    @Input({required: true}) public documentableType: DocumentParentType;
    @Input({required: true}) public documentableId: string | number;
    @Input() public embedded = false;
    @Input() public showHeading = true;

    public readonly categories = operationalDocumentCategories;
    public documents: OperationalDocument[] = [];
    public filterCategory = '';
    public filterTitle = '';
    public filtersOpen = false;
    public loading = false;
    public readonly canView: boolean;
    public readonly canManageVisibility: boolean;
    public readonly visibilitySaving = new Set<string>();

    public constructor(
        private documentsService: DocumentsService,
        private dialog: MatDialog,
        private snackBar: MatSnackBar,
        private permissionsService: PermissionsService,
    )
    {
        this.canView = this.permissionsService.can('view_documents');
        this.canManageVisibility = this.permissionsService.can('manage_document_visibility');
    }

    public ngOnChanges(changes: SimpleChanges): void
    {
        if ((changes['documentableType'] || changes['documentableId']) && this.documentableType && this.documentableId) {
            if (!this.canView) {
                return;
            }

            this.loadDocuments();
        }
    }

    public get filteredDocuments(): OperationalDocument[]
    {
        const title = this.filterTitle.trim().toLocaleLowerCase();

        return this.documents.filter((document) =>
            (!this.filterCategory || document.category === this.filterCategory)
            && (!title || `${document.title} ${document.file_name}`.toLocaleLowerCase().includes(title))
        );
    }

    public get appliedFilterCount(): number
    {
        return Number(Boolean(this.filterCategory)) + Number(Boolean(this.filterTitle.trim()));
    }

    public clearFilters(): void
    {
        this.filterCategory = '';
        this.filterTitle = '';
    }

    public toggleFilters(): void
    {
        this.filtersOpen = !this.filtersOpen;
    }

    public openDocumentDialog(document?: OperationalDocument): void
    {
        this.dialog.open(DocumentUploadDialogComponent, {
            width: '720px',
            maxWidth: 'calc(100vw - 32px)',
            panelClass: 'mobile-width',
            disableClose: true,
            data: {
                documentableType: this.documentableType,
                documentableId: this.documentableId,
                document,
            },
        }).afterClosed().subscribe((document: OperationalDocument | undefined) => {
            if (document) {
                this.upsertDocument(document);
            }
        });
    }

    public async download(document: OperationalDocument): Promise<void>
    {
        try {
            const response = await firstValueFrom(this.documentsService.download(document.id));
            const tab = window.open(response.data.url, '_blank');
            if (tab) {
                tab.opener = null;
            }
        } catch (error) {
            console.error(error);
            this.snackBar.open('There was a problem preparing the download.', 'Ok');
        }
    }

    public delete(document: OperationalDocument): void
    {
        this.dialog.open(DialogComponent, {
            width: '420px',
            data: {
                title: 'Delete document?',
                message: `This will permanently remove <strong>${document.title || document.file_name}</strong>.`,
                type: 'danger',
                confirmButtonLabel: 'Delete',
            },
        }).afterClosed().subscribe(async (confirmed: boolean) => {
            if (!confirmed) {
                return;
            }

            try {
                await firstValueFrom(this.documentsService.delete(document.id));
                this.documents = this.documents.filter((item) => item.id !== document.id);
                this.snackBar.open('Document deleted.', 'Ok');
            } catch (error) {
                console.error(error);
                this.snackBar.open('There was a problem deleting the document.', 'Ok');
            }
        });
    }

    public formatSize(size: number | null): string
    {
        if (size === null) {
            return 'Unknown size';
        }

        if (size < 1024 * 1024) {
            return `${Math.max(1, Math.round(size / 1024))} KB`;
        }

        return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    }

    public uploadedBy(document: OperationalDocument): string
    {
        return document.author?.name ?? 'Unknown user';
    }

    public visibilityValues(document: OperationalDocument): DocumentVisibility[]
    {
        return [
            document.visible_to_customer ? 'customer' : null,
            document.visible_on_app ? 'app' : null,
            document.include_in_report ? 'report' : null,
        ].filter((value): value is DocumentVisibility => value !== null);
    }

    public async updateVisibility(document: OperationalDocument, values: DocumentVisibility[]): Promise<void>
    {
        if (!this.canManageVisibility || this.visibilitySaving.has(document.id)) {
            return;
        }

        const selected = new Set(values);
        const changes = {
            visible_to_customer: selected.has('customer'),
            visible_on_app: selected.has('app'),
            include_in_report: selected.has('report'),
        };

        if (document.visible_to_customer === changes.visible_to_customer
            && document.visible_on_app === changes.visible_on_app
            && document.include_in_report === changes.include_in_report) {
            return;
        }

        this.visibilitySaving.add(document.id);
        this.upsertDocument({...document, ...changes});

        try {
            const response = await firstValueFrom(this.documentsService.update(document.id, changes));
            this.upsertDocument(response.data);
        } catch (error) {
            console.error(error);
            this.upsertDocument(document);
            this.snackBar.open('There was a problem updating document visibility.', 'Ok');
        } finally {
            this.visibilitySaving.delete(document.id);
        }
    }

    private loadDocuments(): void
    {
        this.loading = true;
        this.documentsService.list(this.documentableType, String(this.documentableId)).pipe(
            finalize(() => this.loading = false),
        ).subscribe({
            next: (response) => this.documents = response.data,
            error: (error) => {
                console.error(error);
                this.snackBar.open('There was a problem loading documents.', 'Ok');
            },
        });
    }

    private upsertDocument(document: OperationalDocument): void
    {
        const index = this.documents.findIndex((item) => item.id === document.id);
        if (index === -1) {
            this.documents = [document, ...this.documents];

            return;
        }

        this.documents = this.documents.map((item) => item.id === document.id ? document : item);
    }
}
